import { useCallback } from 'react';

import { pdf } from '@react-pdf/renderer';

import { useI18n } from '@change-corgi/core/react/i18n';

import { usePetitionQRCode } from 'src/app/shared/hooks/qrcode';

import type { Context, Petition, Template } from '../../types';
import { useFlyerImages } from '../useFlyerImages';
import { useFlyerShare } from '../useFlyerShare';

import { FlyerCPDF } from './components/FlyerCPDF';
import { usePetitionTitleImage } from './hooks/usePetitionTitleImage';

type UseFlyerCArgs = { petition: Petition; context: Context };
export function useFlyerC({ petition, context }: UseFlyerCArgs): Template {
	const { translate } = useI18n();

	const share = useFlyerShare({ petition, context, content: 'flyer-c' });
	const shareURL = share.shortUrl ?? share.url;

	// qrcode size is calculated by multiplying the width in the PDF by 3 (for 300DPI)
	const qrCodeState = usePetitionQRCode({
		slugOrId: String(petition.id),
		type: 'WEB_STARTER_DASHBOARD_FLYER_C',
	});
	const qrCode = qrCodeState.data?.imageBase64Uri ?? '';

	const petitionTitleState = usePetitionTitleImage({ id: petition.id, title: petition.ask });
	const petitionTitle = petitionTitleState.data ?? '';
	const petitionImageSrc = petition.photo?.petitionLarge.url;

	const enabled = !!petitionImageSrc;

	const generatePDF = useCallback(async () => {
		if (!enabled) return undefined;
		const props = {
			petitionImageSrc,
			petitionTitleDataURL: petitionTitle,
			qrCodeDataURL: qrCode,
			scanToSign: translate('corgi.petition.starter_dashboard.grow_petition.share_petition_offline.flyer_qrcode_label'),
		};
		const blob = pdf(<FlyerCPDF {...props} />).toBlob();

		return blob;
	}, [enabled, petitionImageSrc, petitionTitle, qrCode, translate]);

	const filename = translate('corgi.petition.starter_dashboard.shared.flyer.filename', { templateName: 'flyer-c' });

	const images = useFlyerImages('flyer-c');

	return {
		name: 'flyer-c',
		enabled,
		images,
		filename,
		shareURL,
		loading: qrCodeState.isPending || petitionTitleState.isPending,
		track: share.action,
		generate: generatePDF,
	};
}
